import {useMemo} from "react";
import type {Order} from "../../../../domain/entities/Order";
import {checkoutCopy, checkoutLocale} from "../../../i18n/checkout/es";
import styles from "../../../pages/checkout/CheckoutPage.module.css";

const formatOrderDate = (date: Date) =>
    new Intl.DateTimeFormat(checkoutLocale, {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(date);

const OrderSummaryHeader = ({code, createdAt}: { code: string; createdAt: Date }) => {
    const orderDate = useMemo(() => formatOrderDate(createdAt), [createdAt]);

    return (
        <header className={styles.orderHeader}>
            <h1 id="order-summary-title" className={styles.orderNumber}>
                {checkoutCopy.order.headingPrefix} {code}
            </h1>
            <span className={styles.orderDate} aria-label={checkoutCopy.order.dateLabel}>
                {orderDate}
            </span>
        </header>
    );
};

const OrderItemsList = ({items, formatter}: { items: Order["items"]; formatter: Intl.NumberFormat }) => (
    <div className={styles.orderItems}>
        {items.map((item) => (
            <div key={item.code} className={styles.orderItem}>
                <div className={styles.itemInfo}>
                    <span className={styles.itemName}>{item.name}</span>
                </div>
                <span className={styles.itemPrice}>{formatter.format(item.price.amount)}</span>
            </div>
        ))}
    </div>
);

const OrderSummaryTotal = ({total, formatter}: { total: Order["amount"]; formatter: Intl.NumberFormat }) => (
    <footer className={styles.orderSummary}>
        <div className={`${styles.summaryRow} ${styles.summaryRowTotal}`}>
            <span>{checkoutCopy.order.total}</span>
            <span>{formatter.format(total.amount)}</span>
        </div>
    </footer>
);

interface OrderSummarySectionProps {
    order: Order;
}

export const OrderSummarySection = ({order}: OrderSummarySectionProps) => {
    const currencyFormatter = useMemo(
        () =>
            new Intl.NumberFormat(checkoutLocale, {
                style: "currency",
                currency: order.amount.currency,
            }),
        [order.amount.currency],
    );

    return (
        <section className={styles.orderSection} aria-labelledby="order-summary-title">
            <OrderSummaryHeader code={order.code} createdAt={order.createdAt}/>
            <OrderItemsList items={order.items} formatter={currencyFormatter}/>
            <OrderSummaryTotal total={order.amount} formatter={currencyFormatter}/>
        </section>
    );
};
